import React, { useState, useRef } from 'react';
import { motion, useMotionValue, useTransform, PanInfo } from 'framer-motion';
import { RefreshCw } from 'lucide-react';

interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  children: React.ReactNode;
}

const THRESHOLD = 80;

const PullToRefresh: React.FC<PullToRefreshProps> = ({ onRefresh, children }) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const y = useMotionValue(0);
  const pullProgress = useTransform(y, [0, THRESHOLD], [0, 1]);
  const rotation = useTransform(pullProgress, [0, 1], [0, 180]);

  const handleDragEnd = async (_: any, info: PanInfo) => {
    if (info.offset.y >= THRESHOLD && !isRefreshing) {
      setIsRefreshing(true);
      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
      }
    }
  };

  const handleDrag = (_: any, info: PanInfo) => {
    // Only allow pull when at top of scroll
    if (containerRef.current && containerRef.current.scrollTop === 0) {
      y.set(Math.max(0, Math.min(info.offset.y, THRESHOLD * 1.5)));
    }
  };

  return (
    <div ref={containerRef} className="h-full overflow-hidden relative">
      {/* Pull indicator */}
      <motion.div
        className="absolute top-0 left-0 right-0 flex justify-center items-center z-10 pointer-events-none"
        style={{
          height: y,
          opacity: pullProgress,
        }}
      >
        <motion.div
          style={{ rotate: isRefreshing ? undefined : rotation }}
          animate={isRefreshing ? { rotate: 360 } : undefined}
          transition={isRefreshing ? { repeat: Infinity, duration: 1, ease: 'linear' } : undefined}
          className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center backdrop-blur-sm"
        >
          <RefreshCw className="w-4 h-4 text-luxury-pinkBright" />
        </motion.div>
      </motion.div>

      {/* Content */}
      <motion.div
        drag="y"
        dragConstraints={{ top: 0, bottom: 0 }}
        dragElastic={0.5}
        onDrag={handleDrag}
        onDragEnd={handleDragEnd}
        style={{ y: isRefreshing ? THRESHOLD / 2 : 0 }}
        className="h-full"
      >
        {children}
      </motion.div>

      {/* Refreshing overlay */}
      {isRefreshing && (
        <div className="absolute top-0 left-0 right-0 h-12 flex items-center justify-center bg-gradient-to-b from-black/50 to-transparent">
          <div className="flex items-center gap-2 text-sm text-white/70">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
            >
              <RefreshCw className="w-4 h-4 text-luxury-pinkBright" />
            </motion.div>
            <span>Atualizando...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PullToRefresh;
